<template>
  <div class="bien-step-wizard">
    <div class="step-header">
      <h3>Sélectionner ou créer un bien</h3>
      <p>Choisissez un bien existant ou créez-en un nouveau</p>
    </div>

    <div class="bien-options">
      <div class="option-tabs">
        <button 
          :class="['tab-btn', { active: activeTab === 'existing' }]"
          @click="activeTab = 'existing'"
        >
          <i class="icon-search"></i>
          Sélectionner un bien existant
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'new' }]"
          @click="activeTab = 'new'"
        >
          <i class="icon-plus"></i>
          Créer un nouveau bien
        </button>
      </div>

      <!-- Existing Property Selection -->
      <div v-if="activeTab === 'existing'" class="tab-content">
        <div class="search-section">
          <div class="search-bar">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher par adresse, référence, ou description..."
              class="search-input"
              @input="debouncedSearch"
            />
            <i class="search-icon">🔍</i>
          </div>

          <div class="filters">
            <select v-model="typeFilter" class="filter-select" @change="searchBiens">
              <option value="">Tous les types</option>
              <option value="apartment">Appartement</option>
              <option value="shop">Magasin</option>
              <option value="office">Bureau</option>
              <option value="house">Maison</option>
              <option value="land">Terrain</option>
            </select>
            <select v-model="disponibiliteFilter" class="filter-select" @change="searchBiens">
              <option value="">Toutes disponibilités</option>
              <option value="disponible">Disponible</option>
              <option value="occupe">Occupé</option>
              <option value="maintenance">En maintenance</option>
            </select>
          </div>
        </div>

        <div class="biens-list" v-if="!loading">
          <div v-if="biens.length === 0" class="no-results">
            <p>Aucun bien trouvé</p>
            <button @click="activeTab = 'new'" class="btn-create-new">
              Créer un nouveau bien
            </button>
          </div>
          
          <div v-else class="bien-grid">
            <div 
              v-for="bien in biens.filter(b => b && b.id)" 
              :key="bien.id"
              :class="[
                'bien-card', 
                { 
                  selected: selectedBienId === bien.id,
                  occupied: bien.status === 'rented' || bien.statut === 'occupe',
                  disabled: bien.status === 'rented' || bien.statut === 'occupe'
                }
              ]"
              @click="selectBien(bien)"
            >
              <div class="bien-info">
                <div class="bien-header">
                  <h4>{{ bien.address || bien.adresse || 'Adresse non spécifiée' }}</h4>
                  <span class="bien-type">{{ getTypeLabel(bien.type) }}</span>
                </div>
                <div class="bien-details">
                  <p><strong>Surface:</strong> {{ bien.surface ? bien.surface + ' m²' : 'Non spécifiée' }}</p>
                  <p><strong>Prix:</strong> {{ (bien.price || bien.prix_location) ? formatPrice(bien.price || bien.prix_location) + ' DH/mois' : 'Non spécifié' }}</p>
                  <p><strong>Ville:</strong> {{ bien.city || 'Non spécifiée' }}</p>
                  <p><strong>Statut:</strong> 
                    <span :class="['status-badge', bien.status || bien.statut]">
                      {{ getStatusLabel(bien.status || bien.statut) }}
                    </span>
                  </p>
                  <p v-if="bien.description"><strong>Description:</strong> {{ bien.description.substring(0, 100) }}{{ bien.description.length > 100 ? '...' : '' }}</p>
                </div>
              </div>

            </div>
          </div>

          <div v-if="pagination.total > pagination.per_page" class="pagination">
            <button 
              @click="loadPage(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="pagination-btn"
            >
              Précédent
            </button>
            <span class="pagination-info">
              Page {{ pagination.current_page }} sur {{ Math.ceil(pagination.total / pagination.per_page) }}
            </span>
            <button 
              @click="loadPage(pagination.current_page + 1)"
              :disabled="pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)"
              class="pagination-btn"
            >
              Suivant
            </button>
          </div>
        </div>

        <div v-else class="loading">
          <p>Chargement des biens...</p>
        </div>
      </div>

      <!-- New Property Creation -->
      <div v-if="activeTab === 'new'" class="tab-content">
        <form @submit.prevent="createBien" class="bien-form">
          <div class="form-section">
            <h4>Informations générales</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Projet *</label>
                <input type="text" v-model="newBien.project" required placeholder="Nom du projet" />
              </div>
              <div class="form-group">
                <label>Type de bien *</label>
                <select v-model="newBien.type" required>
                  <option value="">Sélectionner un type</option>
                  <option value="commercial">Commercial</option>
                  <option value="office">Bureau</option>
                  <option value="apartment">Appartement</option>
                  <option value="App">App</option>
                  <option value="P.Bureau">P.Bureau</option>
                  <option value="Studio">Studio</option>
                  <option value="Mag">Mag</option>
                  <option value="ParkG">ParkG</option>
                  <option value="SL.Sport">SL.Sport</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h4>Localisation</h4>
            <div class="form-group">
              <label>Adresse *</label>
              <input type="text" v-model="newBien.address" required placeholder="Adresse complète du bien" />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Ville *</label>
                <input type="text" v-model="newBien.city" required placeholder="Ville" />
              </div>
              <div class="form-group">
                <label>Code postal *</label>
                <input type="text" v-model="newBien.postal_code" required placeholder="Code postal" />
              </div>
            </div>
          </div>

          <div class="form-section">
            <h4>Caractéristiques</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Surface (m²) *</label>
                <input type="number" v-model="newBien.surface" required min="1" placeholder="Surface en m²" />
              </div>
              <div class="form-group">
                <label>Prix de location (DH/mois) *</label>
                <input type="number" v-model="newBien.price" required min="0" placeholder="Prix mensuel" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Statut *</label>
                <select v-model="newBien.status" required>
                  <option value="available">Disponible</option>
                  <option value="rented">Loué</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h4>Description</h4>
            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="newBien.description" 
                rows="4" 
                placeholder="Description détaillée du bien (équipements, caractéristiques spéciales, etc.)"
              ></textarea>
            </div>
          </div>

          <div class="form-section">
            <h4>Documents (optionnel)</h4>
            <div class="file-uploads">
              <div class="file-group">
                <label>Image du bien</label>
                <input type="file" @change="handleImageUpload" accept=".jpg,.jpeg,.png,.gif" />
              </div>
              <div class="file-group">
                <label>Plan du bien</label>
                <input type="file" @change="handlePlanUpload" accept=".pdf,.jpg,.jpeg,.png" />
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="isCreating">
              {{ isCreating ? 'Création...' : 'Créer le bien' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Selected Property Display -->
    <div v-if="selectedBien" class="selected-bien">
      <h4>Bien sélectionné:</h4>
      <div class="bien-summary">
        <strong>{{ selectedBien.address || selectedBien.adresse }}</strong>
        <span>{{ getTypeLabel(selectedBien.type) }}</span>
        <span>{{ formatPrice(selectedBien.price || selectedBien.prix_location) }} DH/mois</span>
        <span>{{ selectedBien.surface ? selectedBien.surface + ' m²' : '' }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Occupied/disabled property cards */
.bien-card.occupied,
.bien-card.disabled {
  opacity: 0.6;
  background-color: #f5f5f5;
  cursor: not-allowed;
  border: 2px solid #ccc;
}

.bien-card.occupied:hover,
.bien-card.disabled:hover {
  transform: none;
  box-shadow: none;
}

.bien-card.occupied .status-badge.rented,
.bien-card.disabled .status-badge.rented {
  background-color: #dc3545;
  color: white;
}

/* Available property highlighting */
.bien-card:not(.occupied):not(.disabled) {
  cursor: pointer;
}

.bien-card:not(.occupied):not(.disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.bien-card.selected {
  border: 2px solid #007bff;
  background-color: #f8f9fa;
}
</style>

<script>
import bienService from '@/services/bienService'

export default {
  name: 'BienStepWizard',
  props: {
    selectedBien: {
      type: Object,
      default: null
    }
  },
  emits: ['bien-selected', 'next', 'previous'],
  data() {
    return {
      activeTab: 'existing',
      searchQuery: '',
      typeFilter: '',
      disponibiliteFilter: 'disponible', // Default to available properties only for contract creation
      biens: [],
      selectedBienId: null,
      loading: false,
      isCreating: false,
      searchTimeout: null,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0
      },
      newBien: {
        project: '',
        type: '',
        surface: '',
        price: '',
        address: '',
        city: '',
        postal_code: '',
        status: 'available',
        description: '',
        image: null,
        plan_file: null
      }
    }
  },
  mounted() {
    this.loadBiens()
  },
  methods: {
    async loadBiens(page = 1) {
      this.loading = true
      try {
        const filters = {}
        if (this.searchQuery) filters.search = this.searchQuery
        if (this.typeFilter) filters.type = this.typeFilter
        if (this.disponibiliteFilter) {
          // Map frontend values to backend values
          const statusMap = {
            'disponible': 'available',
            'occupe': 'rented',
            'maintenance': 'maintenance'
          }
          filters.status = statusMap[this.disponibiliteFilter] || this.disponibiliteFilter
        }

        const response = await bienService.getBiens(page, this.pagination.per_page, filters)
        
        // Handle Laravel paginated response
        if (response && response.data && Array.isArray(response.data.data)) {
          // Laravel paginated response: response.data.data contains the actual array
          this.biens = response.data.data.filter(bien => bien && bien.id)
          this.pagination = {
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0
          }
        } else if (response && Array.isArray(response)) {
          // Simple array response (direct array fallback)
          this.biens = response.filter(bien => bien && bien.id)
          this.pagination = { current_page: 1, per_page: 10, total: response.length }
        } else {
          // Handle unexpected response format
          console.warn('Unexpected response format:', response)
          this.biens = []
          this.pagination = { current_page: 1, per_page: 10, total: 0 }
        }
      } catch (error) {
        console.error('Error loading biens:', error)
        this.biens = []
      } finally {
        this.loading = false
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.searchBiens()
      }, 300)
    },

    searchBiens() {
      this.loadBiens(1)
    },

    loadPage(page) {
      this.loadBiens(page)
    },

    selectBien(bien) {
      // Prevent selection of occupied properties
      if (bien.status === 'rented' || bien.statut === 'occupe') {
        alert('Ce bien est déjà occupé et ne peut pas être sélectionné pour un nouveau contrat.')
        return
      }
      
      this.selectedBienId = bien.id
      this.$emit('bien-selected', bien)
    },

    async createBien() {
      this.isCreating = true
      try {
        const bienData = { ...this.newBien }
        const response = await bienService.createBien(bienData)
        
        if (response.success) {
          const newBien = response.data
          this.selectBien(newBien)
          this.activeTab = 'existing'
          this.loadBiens() // Refresh the list
          
          // Reset form
          this.resetNewBienForm()
        }
      } catch (error) {
        console.error('Error creating bien:', error)
        alert('Erreur lors de la création du bien')
      } finally {
        this.isCreating = false
      }
    },



    resetNewBienForm() {
      this.newBien = {
        project: '',
        type: '',
        surface: '',
        price: '',
        address: '',
        city: '',
        postal_code: '',
        status: 'available',
        description: '',
        image: null,
        plan_file: null
      }
    },

    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.newBien.image = file
      }
    },

    handlePlanUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.newBien.plan_file = file
      }
    },

    getTypeLabel(type) {
      const types = {
        'commercial': 'Commercial',
        'office': 'Bureau',
        'apartment': 'Appartement',
        'App': 'App',
        'P.Bureau': 'P.Bureau',
        'Studio': 'Studio',
        'Mag': 'Mag',
        'ParkG': 'ParkG',
        'SL.Sport': 'SL.Sport'
      }
      return types[type] || type
    },

    getStatusLabel(status) {
      const statuses = {
        'available': 'Disponible',
        'rented': 'Occupé',
        'maintenance': 'En maintenance',
        // Backward compatibility
        'disponible': 'Disponible',
        'occupe': 'Occupé'
      }
      return statuses[status] || status
    },

    getStatutLabel(statut) {
      // Backward compatibility method
      return this.getStatusLabel(statut)
    },



    formatPrice(price) {
      if (!price) return ''
      return new Intl.NumberFormat('fr-FR').format(price)
    }
  }
}
</script>

<style scoped>
.bien-step-wizard {
  height: 100%;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #f8fafc;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.step-header {
  text-align: center;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.step-header h3 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.step-header p {
  margin: 0;
  color: #6b7280;
}

.bien-options {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.option-tabs {
  display: flex;
  margin-bottom: 0;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  flex-shrink: 0;
}

.tab-btn {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: rgba(203, 184, 124, 0.1);
}

.tab-btn.active {
  color: #22305a !important;
  border-bottom-color: #cbb87c !important;
  background: rgba(203, 184, 124, 0.05);
}

.tab-content {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.search-section {
  margin-bottom: 24px;
}

.search-bar {
  position: relative;
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters {
  display: flex;
  gap: 16px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
}

.biens-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 200px;
  width: 100%;
}

.no-results {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
}

.btn-create-new {
  margin-top: 16px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.bien-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.bien-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-wrap: break-word;
  box-sizing: border-box;
}

.bien-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.bien-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.bien-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.bien-header h4 {
  margin: 0;
  color: #1f2937;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  margin-right: 12px;
}

.bien-type {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.bien-details p {
  margin: 4px 0;
  font-size: 0.875rem;
  color: #6b7280;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.status-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.disponible {
  background: #dcfce7;
  color: #166534;
}

.status-badge.occupe {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.maintenance {
  background: #fee2e2;
  color: #991b1b;
}



.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.loading {
  text-align: center;
  padding: 48px;
  color: #6b7280;
}

.bien-form {
  max-width: 600px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.form-section {
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #22305a;
}

.form-section h4 {
  margin: 0 0 12px 0;
  color: #22305a;
  font-size: 1rem;
  font-weight: 600;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.form-section .form-group:last-child,
.form-section .form-row:last-child {
  margin-bottom: 0;
}

.form-group label {
  margin-bottom: 4px;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.2s;
}

.checkbox-item:hover {
  background: #f9fafb;
}

.file-uploads {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.file-group {
  display: flex;
  flex-direction: column;
}

.file-group label {
  margin-bottom: 4px;
  font-weight: 500;
  color: #374151;
}

.file-group input[type="file"] {
  padding: 8px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 20px 0;
  border-top: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  bottom: 0;
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
}

.btn-primary {
  padding: 12px 32px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.selected-bien {
  margin-top: 32px;
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #0284c7;
  border-radius: 8px;
}

.selected-bien h4 {
  margin: 0 0 8px 0;
  color: #0c4a6e;
}

.bien-summary {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.bien-summary span {
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #0c4a6e;
  white-space: nowrap;
}

/* Custom scrollbar styling */
.tab-content::-webkit-scrollbar,
.biens-list::-webkit-scrollbar {
  width: 6px;
}

.tab-content::-webkit-scrollbar-track,
.biens-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tab-content::-webkit-scrollbar-thumb,
.biens-list::-webkit-scrollbar-thumb {
  background: #cbb87c;
  border-radius: 3px;
}

.tab-content::-webkit-scrollbar-thumb:hover,
.biens-list::-webkit-scrollbar-thumb:hover {
  background: #22305a;
}

/* Form section variations */
.form-section:nth-child(2) {
  border-left-color: #cbb87c;
}

.form-section:nth-child(3) {
  border-left-color: #10b981;
}

.form-section:nth-child(4) {
  border-left-color: #f59e0b;
}

/* Responsive Design */
@media (max-width: 768px) {
  .bien-step-wizard {
    max-height: 85vh;
    padding: 16px;
  }
  
  .step-header {
    padding: 16px;
    margin-bottom: 16px;
  }
  
  .tab-content {
    padding: 16px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .bien-form {
    max-width: 100%;
  }
  
  .form-section {
    margin-bottom: 12px;
    padding: 12px;
  }
  
  .form-actions {
    padding: 16px;
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
  }
}

/* Loading state improvements */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  flex: 1;
}
</style>